import React, { useContext } from 'react';
import { PropTypes } from 'prop-types';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import jsCookie from 'js-cookie';
import { useErrorHandler } from 'react-error-boundary';

// Comopnents
import ErrorHandler from '../ErrorHandler';
import SubmitButton from '@atoms/SubmitButton';
import FormSuccess from '@molecules/FormSuccess/index';
import FormError from '@molecules/FormError/index';
import LoadingInline from '@molecules/LoadingInline';
import { Instructions } from '@styled';

// Functionality
import { transformFormToViewer, transformViewerToForm } from '../../../helpers/editForm.helpers';
import { GlobalModalContext } from '@context/globalModalContext';
import useViewer from '@hooks/useViewer';
import useViewerUpdate from '@hooks/useViewerUpdate';

/**
 * Used for various form modals that open from profile strength carousel
 */
const EditProfileModal = ({
    childComponent,
    validationSchema,
    headerText,
    instructions,
    submitButtonText = 'Save',
    showSubmitButton = true,
}) => {
    const { loading, error, viewer } = useViewer();
    useErrorHandler(error);

    const [isModalOpen, setIsModalOpen] = useContext(GlobalModalContext);
    const {
        updateViewer,
        error: updateError,
        isLoading: isLoadingUpdate,
        isSuccessful: isSuccessUpdate,
    } = useViewerUpdate({
        onCompleted: () => {
            jsCookie.set('my_icsc_updated', 1);
            setTimeout(() => {
                setIsModalOpen(false);
            }, 3000);
        },
    });

    const onSubmit = values => {
        const newViewer = transformFormToViewer(values, viewer);
        updateViewer({ variables: newViewer });
    };

    const getInitialValues = () => {
        if (!viewer) return {};

        try {
            return transformViewerToForm(viewer);
        } catch (e) {
            // console.log(e);
        }
    };

    if (isSuccessUpdate) {
        return <FormSuccess text="Your updates have been saved." />;
    }

    if (updateError) {
        return <FormError text="Your updates have not been saved." />;
    }

    if (!viewer) return null;

    return (
        <ErrorHandler>
            <h3>{headerText}</h3>
            {instructions && <Instructions>{instructions}</Instructions>}
            {loading && <LoadingInline />}
            {!loading && (
                <Formik
                    onSubmit={onSubmit}
                    enableReinitialize
                    initialValues={getInitialValues()}
                    validationSchema={Yup.object(validationSchema)}
                >
                    {formik => (
                        <Form noValidate>
                            {!isSuccessUpdate && (
                                <>
                                    {childComponent}
                                    {showSubmitButton && (
                                        <SubmitButton isSubmitting={isLoadingUpdate}>{submitButtonText}</SubmitButton>
                                    )}
                                </>
                            )}
                        </Form>
                    )}
                </Formik>
            )}
        </ErrorHandler>
    );
};

EditProfileModal.propTypes = {
    childComponent: PropTypes.element,
    validationSchema: PropTypes.object,
    headerText: PropTypes.string,
    instructions: PropTypes.string,
    submitButtonText: PropTypes.string,
    showSubmitButton: PropTypes.bool,
};

export default EditProfileModal;
